import clsx from 'clsx';
import { forwardRef, type KeyboardEvent } from 'react';

/**
 * The search box under the brand in the staff menu (desktop sidebar and the phone drawer): dark like the menu,
 * 44 px tall on a phone and the menu rows' height on a desktop, with a clear button once there is text. Enter opens
 * the first match (the layout decides which), Escape clears the text (an empty box lets Escape close the drawer).
 */
export const NavSearch = forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (value: string) => void;
    onEnter: () => void;
    placeholder: string;
    autoFocus?: boolean;
    className?: string;
  }
>(function NavSearch({ value, onChange, onEnter, placeholder, autoFocus, className }, ref) {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter();
    } else if (e.key === 'Escape' && value) {
      e.preventDefault();
      // With text in the box Escape only clears it; the drawer's own Escape (close) must not fire as well.
      e.stopPropagation();
      onChange('');
    }
  };
  return (
    <div className={clsx('relative px-3', className)}>
      <svg aria-hidden viewBox="0 0 20 20" className="pointer-events-none absolute left-5.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="8.5" cy="8.5" r="5.5" />
        <path d="M12.5 12.5 17 17" strokeLinecap="round" />
      </svg>
      <input
        ref={ref}
        type="search"
        aria-label="Search pages"
        data-testid="nav-search"
        placeholder={placeholder}
        value={value}
        maxLength={60}
        autoFocus={autoFocus}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        enterKeyHint="go"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className="h-11 w-full rounded-md bg-white/10 pl-9 pr-9 text-sm text-white ring-1 ring-white/15 placeholder:text-slate-400 focus:bg-white/15 focus:outline-none focus:ring-2 focus:ring-brand-400 lg:h-9 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute inset-y-0 right-3 flex w-9 items-center justify-center text-lg leading-none text-slate-300 hover:text-white"
        >
          ×
        </button>
      )}
    </div>
  );
});
